import React, { useEffect, useState } from 'react';
import {
  Flex,
  FormGroup,
  Grid,
  openNotificationWithIcon,
  PageContent,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { ErrorMessage, Formik, getIn } from 'formik';
import { object, string, boolean as yupBoolean } from 'yup';
import Form from 'formik-antd/lib/form';
import Input from 'formik-antd/lib/input';
import Select from 'formik-antd/lib/select';
import SubmitButton from 'jsx/common/SubmitButton';
import Button from 'jsx/common/Button';
import { Checkbox, Input as AntdInput, notification, Spin, Tag, Tooltip, Upload } from 'antd';
import { Info } from 'react-feather';
import TimeAgo from 'react-timeago';
import {
  CheckCircleFilled,
  DownloadOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import Fetch from 'jsx/common/util/_fetch';
import { OPCServer, OPCServerGroupDetail } from '..';
import isEmpty from 'lodash/isEmpty';

export type OPCServerSubFormFields = {
  name: string;
  endpoint_url: string;
  port: string;
  security_policy: string;
  message_security_policy: string;
  authentication_mode: string;
  authentication_settings: {
    username?: string;
    password?: string;
  };
};

export type OPCServerGroupFormFields = {
  groupName: string;
  primary: OPCServerSubFormFields;
  hasSecondary: boolean;
  secondary: OPCServerSubFormFields;
};

type ConnectionTestState = { tested: boolean; success: boolean };

type CertFiles = {
  cert: File | null;
  key: File | null;
  keyPassword: string;
  removeCerts: boolean;
};

type ServerSectionProps = {
  prefix: 'primary' | 'secondary';
  values: OPCServerGroupFormFields;
  errors: any;
  handleBlur: React.FocusEventHandler;
  handleChange: React.ChangeEventHandler;
  setFieldValue: (field: string, value: any) => void;
  testState: ConnectionTestState;
  isTesting: boolean;
  setTestState: React.Dispatch<React.SetStateAction<ConnectionTestState>>;
  setIsTesting: React.Dispatch<React.SetStateAction<boolean>>;
  certFiles: CertFiles;
  setCertFiles: React.Dispatch<React.SetStateAction<CertFiles>>;
  hasCertificate: boolean;
  hasPrivateKey: boolean;
  serverId: string;
  lastCheckStatus?: string | null;
  lastCheckedAt?: string | null;
};

const emptyCertFiles = (): CertFiles => ({
  cert: null,
  key: null,
  keyPassword: '',
  removeCerts: false,
});

const fetchMultipart = (
  url: string,
  method: 'POST' | 'PATCH',
  formData: FormData
): Promise<Response> =>
  fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${window.sessionStorage.getItem('token')}`,
    },
    body: formData,
  });

const SECURITY_POLICIES = [
  { value: 'none', label: 'None' },
  { value: 'basic_256_sha_256', label: 'Basic256Sha256' },
  { value: 'aes_128_sha_256_rsa_oaep', label: 'Aes128Sha256RsaOaep' },
  { value: 'aes_256_sha_256_rsa_pss', label: 'Aes256Sha256RsaPss' },
];

const MESSAGE_SECURITY_POLICIES = [
  { value: 'none', label: 'None' },
  { value: 'sign', label: 'Sign' },
  { value: 'sign_and_encrypt', label: 'Sign and Encrypt' },
];

const AUTHENTICATION_MODES = [
  { value: 'anonymous', label: 'Anonymous' },
  { value: 'username_password', label: 'Credentials' },
];

const emptyServerFields = (): OPCServerSubFormFields => ({
  name: '',
  endpoint_url: '',
  port: '',
  security_policy: 'none',
  message_security_policy: 'none',
  authentication_mode: 'anonymous',
  authentication_settings: {
    username: '',
    password: '',
  },
});

const parseEndpointUrl = (endpointUrl: string): { host: string } => {
  // Only accept `opc.tcp://<hostname>` — port must be configured via the
  // separate Port field, never embedded in the URL.
  const match = endpointUrl.match(/^opc\.tcp:\/\/([^:/\s]+)$/);
  if (match) {
    return { host: match[1] };
  }
  return { host: '' };
};

// username/password are omitted entirely for non-credentials modes so a cert PATCH
// does not silently clear previously stored credentials.
const buildServerPayload = (fields: OPCServerSubFormFields): Record<string, any> => {
  const { host } = parseEndpointUrl(fields.endpoint_url);
  const payload: Record<string, any> = {
    name: fields.name?.trim() || '',
    host: (host || '').trim(),
    port: String(fields.port ?? '').trim(),
    security_policy: fields.security_policy,
    message_security_policy: fields.message_security_policy,
    authentication_mode: fields.authentication_mode,
  };
  if (fields.authentication_mode === 'username_password') {
    payload.username = fields.authentication_settings.username ?? '';
    payload.password = fields.authentication_settings.password ?? '';
  }
  return payload;
};

const buildGroupFormData = (
  values: OPCServerGroupFormFields,
  primaryCerts: CertFiles,
  secondaryCerts: CertFiles
): FormData => {
  const fd = new FormData();
  fd.append('name', values.groupName.trim());
  fd.append('primary', JSON.stringify(buildServerPayload(values.primary)));
  if (values.hasSecondary) {
    fd.append('secondary', JSON.stringify(buildServerPayload(values.secondary)));
  }
  if (primaryCerts.cert) fd.append('primary_certificate', primaryCerts.cert);
  if (primaryCerts.key) fd.append('primary_private_key', primaryCerts.key);
  if (primaryCerts.keyPassword) fd.append('primary_key_password', primaryCerts.keyPassword);
  if (values.hasSecondary) {
    if (secondaryCerts.cert) fd.append('secondary_certificate', secondaryCerts.cert);
    if (secondaryCerts.key) fd.append('secondary_private_key', secondaryCerts.key);
    if (secondaryCerts.keyPassword) fd.append('secondary_key_password', secondaryCerts.keyPassword);
  }
  return fd;
};

const buildServerFormData = (
  serverPayload: Record<string, any>,
  certFiles: CertFiles
): FormData => {
  const fd = new FormData();
  Object.entries(serverPayload).forEach(([k, v]) => {
    if (v !== null && v !== undefined) fd.append(k, String(v));
  });
  if (certFiles.removeCerts) fd.append('remove_existing_certificates', 'true');
  if (certFiles.cert) fd.append('certificate', certFiles.cert);
  if (certFiles.key) fd.append('private_key', certFiles.key);
  if (certFiles.keyPassword) fd.append('key_password', certFiles.keyPassword);
  return fd;
};

const buildTestConnectionFormData = (
  payload: Record<string, any>,
  certFiles: CertFiles,
  serverId: string
): FormData => {
  const fd = new FormData();
  fd.append('host', payload.host);
  fd.append('port', String(parseInt(payload.port, 10)));
  fd.append('security_policy', payload.security_policy);
  fd.append('message_security_policy', payload.message_security_policy);
  fd.append('authentication_mode', payload.authentication_mode);
  if (payload.username) fd.append('username', payload.username);
  if (payload.password) fd.append('password', payload.password);
  if (certFiles.cert) fd.append('certificate', certFiles.cert);
  if (certFiles.key) fd.append('private_key', certFiles.key);
  if (certFiles.keyPassword) fd.append('key_password', certFiles.keyPassword);
  if (serverId) fd.append('server_id', serverId);
  return fd;
};


const parseOPCServerToSubForm = (server: OPCServer): OPCServerSubFormFields => {
  // Display URL never includes the port — the Port field is the source of truth.
  const endpointUrl = server.host ? `opc.tcp://${server.host}` : '';
  return {
    name: server.name || '',
    endpoint_url: endpointUrl,
    port: server.port || '',
    security_policy: server.security_policy || 'none',
    message_security_policy: server.message_security_policy || 'none',
    authentication_mode: server.authentication_mode || 'anonymous',
    authentication_settings: {
      username: server.username ?? '',
      password: '',
    },
  };
};

const parseGroupToFormFields = (group: OPCServerGroupDetail): OPCServerGroupFormFields => ({
  groupName: group.name || '',
  primary: parseOPCServerToSubForm(group.primary),
  hasSecondary: !!group.secondary,
  secondary: group.secondary
    ? parseOPCServerToSubForm(group.secondary)
    : emptyServerFields(),
});

const serverSubSchema = () =>
  object().shape({
    name: string()
      .required('Required')
      .trim('Cannot start or end with whitespace')
      .strict(true),
    endpoint_url: string()
      .required('Required')
      .test(
        'endpoint-url-validation',
        'Please enter a valid endpoint URL (e.g., opc.tcp://hostname). ' +
          'The port must be set in the Port field, not in the URL.',
        value => !!value && /^opc\.tcp:\/\/[^:/\s]+$/.test(value)
      ),
    port: string()
      .required('Required')
      .matches(/^\d+$/, 'Port must be a number')
      .test('port-range', 'Port must be between 1 and 65535', value => {
        if (!value) return false;
        const portNum = parseInt(value, 10);
        return portNum >= 1 && portNum <= 65535;
      }),
    security_policy: string().required('Required'),
    message_security_policy: string().required('Required'),
    authentication_mode: string().required('Required'),
    authentication_settings: object().when('authentication_mode', {
      is: 'username_password',
      then: schema =>
        schema.shape({
          username: string().required('Required'),
          password: string().required('Required'),
        }),
      otherwise: schema => schema,
    }),
  });

const OPCServerGroupSchema = () =>
  object().shape({
    groupName: string()
      .required('Required')
      .trim('Cannot start or end with whitespace')
      .strict(true),
    primary: serverSubSchema(),
    hasSecondary: yupBoolean(),
    secondary: object().when('hasSecondary', {
      is: true,
      then: () => serverSubSchema(),
      otherwise: () => object(),
    }),
  });

function AddOPCServer() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { groupId } = useParams<{ groupId: string }>();
  const [t] = useTranslation(['configuration', 'common']);
  const [hasPermission] = useUserinfo();

  const [primaryTestState, setPrimaryTestState] = useState<ConnectionTestState>({
    tested: false,
    success: false,
  });
  const [secondaryTestState, setSecondaryTestState] = useState<ConnectionTestState>({
    tested: false,
    success: false,
  });
  const [isTestingPrimary, setIsTestingPrimary] = useState(false);
  const [isTestingSecondary, setIsTestingSecondary] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isGeneratingCerts, setIsGeneratingCerts] = useState(false);
  const [primaryCertFiles, setPrimaryCertFiles] = useState<CertFiles>(emptyCertFiles());
  const [secondaryCertFiles, setSecondaryCertFiles] = useState<CertFiles>(emptyCertFiles());

  const [{ data: rawGroup, loading }] = useFetch<OPCServerGroupDetail | null>({
    url: `/api/opc-config-server-group/${groupId}/`,
    defaultValue: null,
    initialFetch: !!groupId,
  });

  // Cert files live outside Formik; reset them whenever the group data changes
  // to stay in sync with Formik's own enableReinitialize reset.
  useEffect(() => {
    setPrimaryCertFiles(emptyCertFiles());
    setSecondaryCertFiles(emptyCertFiles());
  }, [rawGroup]);

  const groupFormData: OPCServerGroupFormFields | null = rawGroup
    ? parseGroupToFormFields(rawGroup)
    : null;

  const primaryServerId = rawGroup?.primary?.id || '';
  const secondaryServerId = rawGroup?.secondary?.id || '';

  const editMode = !!(groupId && groupFormData && !loading);
  const disabledEdit = editMode && !hasPermission(['change_opcservergroupconfig']);

  const goToOPCServersList = () => {
    history.push(`${url.replace(`/add`, '').replace(`/${groupId}`, '')}`);
  };

  const onGenerateCertificates = async () => {
    setIsGeneratingCerts(true);
    try {
      const resp = await fetch('/api/opc-config-server-group/generate-certificates/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${window.sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'mss-client.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      } else {
        const data = await resp.json().catch(() => null);
        const detail = data?.detail || t('Failed to generate certificates.');
        openNotificationWithIcon(notification.error, detail, '');
      }
    } catch {
      openNotificationWithIcon(notification.error, t('Failed to generate certificates.'), '');
    } finally {
      setIsGeneratingCerts(false);
    }
  };

  const getValidationErrorMessage = (respData: any): string => {
    if (!respData) return '';
    if (Array.isArray(respData.non_field_errors) && respData.non_field_errors[0])
      return respData.non_field_errors[0];
    if (typeof respData === 'object') {
      const firstKey = Object.keys(respData)[0];
      const firstMessages = firstKey && respData[firstKey];
      if (Array.isArray(firstMessages) && firstMessages[0]) return firstMessages[0];
      if (typeof firstMessages === 'string') return firstMessages;
    }
    return '';
  };

  const handleErrorResponse = async (resp: Response, action: 'edit' | 'add') => {
    let respData: any;
    try {
      respData = await resp.json?.();
    } catch {
      respData = null;
    }
    const isServerError = resp.status >= 500;
    const validationError = getValidationErrorMessage(respData);
    const serverDetail =
      respData?.detail ||
      respData?.error ||
      (typeof respData?.message === 'string' ? respData.message : '');
    if (validationError) {
      openNotificationWithIcon(notification.error, validationError, '');
    } else if (isServerError) {
      const message = serverDetail
        ? t('Server error. Please try again.') + ' ' + serverDetail
        : t('Server error. Please try again.');
      openNotificationWithIcon(notification.error, message, '');
    } else {
      const msg = {
        edit: 'OPC Server group update failed',
        add: 'Failed to create OPC Server group.',
      };
      openNotificationWithIcon(notification.error, t(msg[action]), '');
    }
  };

  const onCreate = async (values: OPCServerGroupFormFields) => {
    setIsLoadingSave(true);
    try {
      const hasCertFiles =
        !!(primaryCertFiles.cert || primaryCertFiles.key) ||
        !!(values.hasSecondary && (secondaryCertFiles.cert || secondaryCertFiles.key));

      let resp: Response;
      if (hasCertFiles) {
        resp = await fetchMultipart(
          '/api/opc-config-server-group/',
          'POST',
          buildGroupFormData(values, primaryCertFiles, secondaryCertFiles)
        );
      } else {
        resp = await Fetch.post(`/api/opc-config-server-group/`, {
          body: JSON.stringify({
            name: values.groupName.trim(),
            primary: buildServerPayload(values.primary),
            secondary: values.hasSecondary ? buildServerPayload(values.secondary) : null,
          }),
        });
      }

      if (resp.status === 201) {
        openNotificationWithIcon(
          notification.success,
          t('OPC Server group created successfully.'),
          ''
        );
        setPrimaryTestState({ tested: false, success: false });
        setSecondaryTestState({ tested: false, success: false });
        goToOPCServersList();
      } else {
        await handleErrorResponse(resp, 'add');
      }
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Failed to create OPC Server group.'),
        ''
      );
    } finally {
      setIsLoadingSave(false);
    }
  };

  const onEdit = async (values: OPCServerGroupFormFields) => {
    setIsLoadingSave(true);

    try {
      const primaryPayload = buildServerPayload(values.primary);
      const primaryNeedsMultipart = !!(
        primaryCertFiles.cert || primaryCertFiles.key || primaryCertFiles.removeCerts
      );

      const primaryRequest = primaryNeedsMultipart
        ? fetchMultipart(
            `/api/opc-config-server/${primaryServerId}/`,
            'PATCH',
            buildServerFormData(primaryPayload, primaryCertFiles)
          )
        : Fetch.patch(`/api/opc-config-server/${primaryServerId}/`, {
            body: JSON.stringify(primaryPayload),
          });

      // Three secondary-related cases handled via the group PATCH:
      //   1. Adding a brand-new secondary to a group that had none.
      //   2. Removing an existing secondary (user unchecked the checkbox).
      //   3. Neither — group PATCH only updates the name.
      // Updating an already-existing secondary is handled separately below via
      // a direct PATCH to the server endpoint.
      const isAddingNewSecondary = !secondaryServerId && values.hasSecondary;
      const isRemovingSecondary = !!secondaryServerId && !values.hasSecondary;

      let groupPatchRequest: Promise<Response>;
      if (isAddingNewSecondary) {
        const secondaryPayload = buildServerPayload(values.secondary);
        const secondaryNeedsMultipart = !!(secondaryCertFiles.cert || secondaryCertFiles.key);
        if (secondaryNeedsMultipart) {
          const fd = new FormData();
          fd.append('name', values.groupName.trim());
          fd.append('secondary', JSON.stringify(secondaryPayload));
          if (secondaryCertFiles.cert) fd.append('secondary_certificate', secondaryCertFiles.cert);
          if (secondaryCertFiles.key) fd.append('secondary_private_key', secondaryCertFiles.key);
          if (secondaryCertFiles.keyPassword) fd.append('secondary_key_password', secondaryCertFiles.keyPassword);
          groupPatchRequest = fetchMultipart(
            `/api/opc-config-server-group/${groupId}/`,
            'PATCH',
            fd
          );
        } else {
          groupPatchRequest = Fetch.patch(`/api/opc-config-server-group/${groupId}/`, {
            body: JSON.stringify({ name: values.groupName.trim(), secondary: secondaryPayload }),
          });
        }
      } else if (isRemovingSecondary) {
        groupPatchRequest = Fetch.patch(`/api/opc-config-server-group/${groupId}/`, {
          body: JSON.stringify({ name: values.groupName.trim(), secondary: null }),
        });
      } else {
        groupPatchRequest = Fetch.patch(`/api/opc-config-server-group/${groupId}/`, {
          body: JSON.stringify({ name: values.groupName.trim() }),
        });
      }

      const requests: Promise<Response>[] = [
        groupPatchRequest,
        primaryRequest,
      ];

      if (secondaryServerId && values.hasSecondary) {
        const secondaryPayload = buildServerPayload(values.secondary);
        const secondaryNeedsMultipart = !!(
          secondaryCertFiles.cert || secondaryCertFiles.key || secondaryCertFiles.removeCerts
        );

        requests.push(
          secondaryNeedsMultipart
            ? fetchMultipart(
                `/api/opc-config-server/${secondaryServerId}/`,
                'PATCH',
                buildServerFormData(secondaryPayload, secondaryCertFiles)
              )
            : Fetch.patch(`/api/opc-config-server/${secondaryServerId}/`, {
                body: JSON.stringify(secondaryPayload),
              })
        );
      }

      const results = await Promise.all(requests);
      const failed = results.find(r => r.status !== 200);

      if (!failed) {
        openNotificationWithIcon(
          notification.success,
          t('OPC Server group updated successfully'),
          ''
        );
        setPrimaryTestState({ tested: false, success: false });
        setSecondaryTestState({ tested: false, success: false });
        goToOPCServersList();
      } else {
        await handleErrorResponse(failed, 'edit');
      }
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('OPC Server group update failed'),
        ''
      );
    } finally {
      setIsLoadingSave(false);
    }
  };

  const onTestConnection = async (
    serverFields: OPCServerSubFormFields,
    certFiles: CertFiles,
    serverId: string,
    setTestState: React.Dispatch<React.SetStateAction<ConnectionTestState>>,
    setIsTesting: React.Dispatch<React.SetStateAction<boolean>>,
    serverLabel: string
  ) => {
    setIsTesting(true);

    const payload = buildServerPayload(serverFields);
    const hasCertFiles = !!(certFiles.cert || certFiles.key);

    try {
      let resp: Response;

      if (hasCertFiles) {
        // Uploaded files take priority; server_id is also sent so the backend
        // can fill whichever side is missing from the stored DB row (hybrid mode).
        resp = await fetchMultipart(
          '/api/opc-config-server/connection-test/',
          'POST',
          buildTestConnectionFormData(payload, certFiles, serverId)
        );
      } else if (serverId) {
        // No local files — load stored cert/key from the existing server row.
        resp = await Fetch.post(`/api/opc-config-server/connection-test/`, {
          body: JSON.stringify({
            host: payload.host,
            port: parseInt(payload.port, 10),
            security_policy: payload.security_policy,
            message_security_policy: payload.message_security_policy,
            authentication_mode: payload.authentication_mode,
            server_id: serverId,
            ...(payload.username ? { username: payload.username } : {}),
            ...(payload.password ? { password: payload.password } : {}),
          }),
        });
      } else {
        resp = await Fetch.post(`/api/opc-config-server/connection-test/`, {
          body: JSON.stringify({
            host: payload.host,
            port: parseInt(payload.port, 10),
            security_policy: payload.security_policy,
            message_security_policy: payload.message_security_policy,
            authentication_mode: payload.authentication_mode,
            username: payload.username,
            password: payload.password,
          }),
        });
      }

      if (resp.status === 200) {
        const data = await resp.json?.();
        if (data?.error) {
          openNotificationWithIcon(
            notification.error,
            `${serverLabel}: ${t('Connection failed')}`,
            data.error
          );
          setTestState({ tested: true, success: false });
        } else {
          openNotificationWithIcon(
            notification.success,
            `${serverLabel}: ${t('Connection successful')}`,
            ''
          );
          setTestState({ tested: true, success: true });
        }
      } else {
        const respData = await resp.json?.();
        const errMsg =
          respData?.error || respData?.detail || respData?.message || 'Connection failed';
        openNotificationWithIcon(
          notification.error,
          `${serverLabel}: ${t('Connection failed')}`,
          t(errMsg)
        );
        setTestState({ tested: true, success: false });
      }
    } catch (error: any) {
      openNotificationWithIcon(
        notification.error,
        `${serverLabel}: ${t('Connection failed')}`,
        error?.message || ''
      );
      setTestState({ tested: true, success: false });
    } finally {
      setIsTesting(false);
    }
  };

  const initialValues: OPCServerGroupFormFields = {
    groupName: groupFormData?.groupName || '',
    primary: groupFormData?.primary || emptyServerFields(),
    hasSecondary: groupFormData?.hasSecondary || false,
    secondary: groupFormData?.secondary || emptyServerFields(),
  };

  const renderServerSection = ({
    prefix,
    values,
    errors,
    handleBlur,
    handleChange,
    setFieldValue,
    testState,
    isTesting,
    setTestState,
    setIsTesting,
    certFiles,
    setCertFiles,
    hasCertificate,
    hasPrivateKey,
    serverId,
    lastCheckStatus,
    lastCheckedAt,
  }: ServerSectionProps) => {
    const serverValues = values[prefix];
    const serverLabel = prefix === 'primary' ? t('Primary server') : t('Secondary server');

    const hasEndpointError = !!getIn(errors, `${prefix}.endpoint_url`);
    const hasPortError = !!getIn(errors, `${prefix}.port`);
    const hasUsernameError = !!getIn(errors, `${prefix}.authentication_settings.username`);
    const hasPasswordError = !!getIn(errors, `${prefix}.authentication_settings.password`);

    const isCredentialsMode = serverValues.authentication_mode === 'username_password';
    // Cert upload is shown when the channel requires OPC UA security (non-none policy/mode).
    const needsCerts =
      serverValues.security_policy !== 'none' || serverValues.message_security_policy !== 'none';

    const testButtonDisabled =
      hasEndpointError ||
      hasPortError ||
      (isCredentialsMode && (hasUsernameError || hasPasswordError)) ||
      disabledEdit ||
      isTesting;

    return (
      <>
        <Grid.Row>
          <Grid.Col width={6}>
            <FormGroup
              label={t('Server name')}
              required={true}
              error={
                <ErrorMessage
                  name={`${prefix}.name`}
                  render={(msg: string) => t(msg)}
                />
              }
              input={
                <Input
                  name={`${prefix}.name`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={serverValues.name}
                  disabled={disabledEdit}
                />
              }
            />
          </Grid.Col>
          <Grid.Col width={6}>
            <FormGroup
              label={t('Endpoint URL Name')}
              required={true}
              error={
                <ErrorMessage
                  name={`${prefix}.endpoint_url`}
                  render={(msg: string) => t(msg)}
                />
              }
              input={
                <Input
                  name={`${prefix}.endpoint_url`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={serverValues.endpoint_url}
                  disabled={disabledEdit}
                  placeholder="opc.tcp://opcserver1"
                />
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col width={6}>
            <FormGroup
              label={t('Security policy')}
              required={true}
              error={
                <ErrorMessage
                  name={`${prefix}.security_policy`}
                  render={(msg: string) => t(msg)}
                />
              }
              input={
                <Select
                  name={`${prefix}.security_policy`}
                  onChange={value => {
                    setFieldValue(`${prefix}.security_policy`, value);
                    if (value === 'none' && serverValues.message_security_policy === 'none') {
                      setCertFiles(emptyCertFiles());
                    }
                  }}
                  onBlur={handleBlur}
                  value={serverValues.security_policy}
                  disabled={disabledEdit}
                  placeholder={t('Select security policy')}
                  style={{ width: '100%' }}
                  options={SECURITY_POLICIES.map(p => ({ value: p.value, label: p.label }))}
                />
              }
            />
          </Grid.Col>
          <Grid.Col width={6}>
            <FormGroup
              label={t('common:Port')}
              required={true}
              error={
                <ErrorMessage
                  name={`${prefix}.port`}
                  render={(msg: string) => t(msg)}
                />
              }
              input={
                <Input
                  name={`${prefix}.port`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={serverValues.port}
                  disabled={disabledEdit}
                  placeholder="34493"
                />
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col width={6}>
            <FormGroup
              label={t('Authentication settings')}
              required={true}
              error={
                <ErrorMessage
                  name={`${prefix}.authentication_mode`}
                  render={(msg: string) => t(msg)}
                />
              }
              input={
                <Select
                  name={`${prefix}.authentication_mode`}
                  onChange={value => {
                    setFieldValue(`${prefix}.authentication_mode`, value);
                  }}
                  onBlur={handleBlur}
                  value={serverValues.authentication_mode}
                  disabled={disabledEdit}
                  placeholder={t('Select one or more')}
                  style={{ width: '100%' }}
                  options={AUTHENTICATION_MODES.map(m => ({
                    value: m.value,
                    label: m.label,
                  }))}
                />
              }
            />
          </Grid.Col>
          <Grid.Col width={6}>
            <FormGroup
              label={t('Message security mode')}
              required={true}
              error={
                <ErrorMessage
                  name={`${prefix}.message_security_policy`}
                  render={(msg: string) => t(msg)}
                />
              }
              input={
                <Select
                  name={`${prefix}.message_security_policy`}
                  onChange={value => {
                    setFieldValue(`${prefix}.message_security_policy`, value);
                    if (value === 'none' && serverValues.security_policy === 'none') {
                      setCertFiles(emptyCertFiles());
                    }
                  }}
                  onBlur={handleBlur}
                  value={serverValues.message_security_policy}
                  disabled={disabledEdit}
                  placeholder={t('Select message security mode')}
                  style={{ width: '100%' }}
                  options={MESSAGE_SECURITY_POLICIES.map(p => ({
                    value: p.value,
                    label: p.label,
                  }))}
                />
              }
            />
          </Grid.Col>
        </Grid.Row>

        {(isCredentialsMode || needsCerts) && (
          <>
            <Grid.Row>
              <Grid.Col width={6}>
                {isCredentialsMode && (
                  <FormGroup
                    label={t('common:Username')}
                    required={true}
                    error={
                      <ErrorMessage
                        name={`${prefix}.authentication_settings.username`}
                        render={(msg: string) => t(msg)}
                      />
                    }
                    input={
                      <Input
                        name={`${prefix}.authentication_settings.username`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={serverValues.authentication_settings.username}
                        disabled={disabledEdit}
                      />
                    }
                  />
                )}
              </Grid.Col>
              <Grid.Col width={6}>
                {needsCerts && (
                  <FormGroup
                    label={t('Certificate')}
                    input={
                      <div>
                        {editMode && hasCertificate && !certFiles.cert && !certFiles.removeCerts && (
                          <div style={{ marginBottom: 8 }}>
                            <Tag icon={<CheckCircleFilled />} color="success">
                              {t('Certificate stored')}
                            </Tag>
                          </div>
                        )}
                        <Upload
                          beforeUpload={file => {
                            setCertFiles(prev => ({ ...prev, cert: file as File }));
                            return false;
                          }}
                          onRemove={() => setCertFiles(prev => ({ ...prev, cert: null }))}
                          fileList={
                            certFiles.cert
                              ? [{ uid: '-1', name: certFiles.cert.name, status: 'done' as const }]
                              : []
                          }
                          maxCount={1}
                          disabled={disabledEdit}
                        >
                          <Button disabled={disabledEdit} variant="default">
                            <UploadOutlined /> {t('Upload certificate')}
                          </Button>
                        </Upload>
                      </div>
                    }
                  />
                )}
              </Grid.Col>
            </Grid.Row>

            <Grid.Row>
              <Grid.Col width={6}>
                {isCredentialsMode && (
                  <FormGroup
                    label={t('common:Password')}
                    required={true}
                    error={
                      <ErrorMessage
                        name={`${prefix}.authentication_settings.password`}
                        render={(msg: string) => t(msg)}
                      />
                    }
                    input={
                      <Input.Password
                        name={`${prefix}.authentication_settings.password`}
                        iconRender={visible =>
                          visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={serverValues.authentication_settings.password}
                        disabled={disabledEdit}
                      />
                    }
                  />
                )}
              </Grid.Col>
              <Grid.Col width={6}>
                {needsCerts && (
                  <FormGroup
                    label={t('Private key')}
                    input={
                      <div>
                        {editMode && hasPrivateKey && !certFiles.key && !certFiles.removeCerts && (
                          <div style={{ marginBottom: 8 }}>
                            <Tag icon={<CheckCircleFilled />} color="success">
                              {t('Private key stored')}
                            </Tag>
                          </div>
                        )}
                        <Upload
                          beforeUpload={file => {
                            setCertFiles(prev => ({ ...prev, key: file as File }));
                            return false;
                          }}
                          onRemove={() => setCertFiles(prev => ({ ...prev, key: null }))}
                          fileList={
                            certFiles.key
                              ? [{ uid: '-1', name: certFiles.key.name, status: 'done' as const }]
                              : []
                          }
                          maxCount={1}
                          disabled={disabledEdit}
                        >
                          <Button disabled={disabledEdit} variant="default">
                            <UploadOutlined /> {t('Upload private key')}
                          </Button>
                        </Upload>
                      </div>
                    }
                  />
                )}
              </Grid.Col>
            </Grid.Row>

            {needsCerts && (
              <Grid.Row>
                <Grid.Col width={6} />
                <Grid.Col width={6}>
                  <FormGroup
                    label={t('Key password')}
                    input={
                      <AntdInput.Password
                        iconRender={visible =>
                          visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                        }
                        value={certFiles.keyPassword}
                        onChange={e =>
                          setCertFiles(prev => ({ ...prev, keyPassword: e.target.value }))
                        }
                        disabled={disabledEdit}
                        placeholder={t('Passphrase for encrypted private key (optional)')}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>
            )}

            {editMode && needsCerts && (hasCertificate || hasPrivateKey) && (
              <Grid.Row>
                <Grid.Col width={6} />
                <Grid.Col width={6}>
                  <FormGroup
                    label=""
                    input={
                      <Checkbox
                        checked={certFiles.removeCerts}
                        disabled={disabledEdit}
                        onChange={e =>
                          setCertFiles(prev => ({ ...prev, removeCerts: e.target.checked }))
                        }
                      >
                        {t('Remove existing certificate and private key')}
                      </Checkbox>
                    }
                  />
                </Grid.Col>
              </Grid.Row>
            )}
          </>
        )}

        <Grid.Row>
          <Grid.Col width={12}>
            <Flex h="end" v="center">
              {lastCheckStatus !== undefined && lastCheckStatus !== null && (
                <Tooltip
                  title={
                    <span style={{ fontSize: 12, lineHeight: 1 }}>
                      {lastCheckStatus === 'OK'
                        ? t('Connected')
                        : lastCheckStatus}
                    </span>
                  }
                  placement="left"
                >
                  <div className="mr-2" style={{ display: 'flex', alignItems: 'center' }}>
                    <Info
                      size={15}
                      className={lastCheckStatus === 'OK' ? 'text-success' : 'text-danger'}
                    />
                  </div>
                </Tooltip>
              )}
              {lastCheckedAt && (
                <small className="mr-4 text-gray-500">
                  {`${t('Last checked')}: `}
                  <strong>
                    <TimeAgo date={lastCheckedAt} />
                  </strong>
                </small>
              )}
              <Button
                disabled={testButtonDisabled}
                variant="primary"
                onClick={() =>
                  onTestConnection(
                    serverValues,
                    certFiles,
                    serverId,
                    setTestState,
                    setIsTesting,
                    serverLabel
                  )
                }
                loading={isTesting}
                data-ta={`test${prefix === 'primary' ? 'Primary' : 'Secondary'}OPCConnection`}
              >
                {t('Test connection')}
              </Button>
            </Flex>
          </Grid.Col>
        </Grid.Row>

      </>
    );
  };

  return loading ? (
    <Flex v="center" h="center">
      <Spin />
    </Flex>
  ) : (
    <PageContent
      title={editMode ? t('Edit OPC Server group') : t('New OPC Server group')}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={OPCServerGroupSchema()}
        enableReinitialize
        onSubmit={(values: OPCServerGroupFormFields) => {
          if (editMode) {
            onEdit(values);
          } else {
            onCreate(values);
          }
        }}
      >
        {({ values, errors, touched, handleBlur, handleChange, setFieldValue, setFieldTouched }) => {
          const testHasErrors =
            (primaryTestState.tested && !primaryTestState.success) ||
            (secondaryTestState.tested && !secondaryTestState.success);
          // When secondary is unchecked, ignore any stale secondary validation
          // errors — they are no longer relevant to the save operation.
          const { secondary: _secondaryErrors, ...errorsWithoutSecondary } = errors as any;
          const activeErrors = values.hasSecondary ? errors : errorsWithoutSecondary;
          const isSaveDisabled =
            isLoadingSave ||
            disabledEdit ||
            (!testHasErrors && !isEmpty(activeErrors)) ||
            !Object.keys(touched).length ||
            isTestingPrimary ||
            isTestingSecondary;

          return (
            <Form>
              {/* Group name */}
              <Grid.Row>
                <Grid.Col width={6}>
                  <h5 className="text-uppercase mb-8">
                    {t('OPC SERVER GROUP')}
                  </h5>
                  <FormGroup
                    label={t('Group name')}
                    required={true}
                    error={
                      <ErrorMessage
                        name="groupName"
                        render={(msg: string) => t(msg)}
                      />
                    }
                    input={
                      <Input
                        name="groupName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.groupName}
                        disabled={disabledEdit}
                        placeholder={t('Group name')}
                      />
                    }
                  />
                </Grid.Col>
              </Grid.Row>

              {/* Generate certificates */}
              <Grid.Row>
                <Grid.Col width={6}>
                  <FormGroup
                    label={t('OPC UA client certificates')}
                    input={
                      <Button
                        variant="default"
                        loading={isGeneratingCerts}
                        disabled={disabledEdit || isGeneratingCerts}
                        onClick={onGenerateCertificates}
                      >
                        <DownloadOutlined /> {t('Generate & download certificates')}
                      </Button>
                    }
                  />
                </Grid.Col>
              </Grid.Row>

              {/* Primary server */}
              <Grid.Row>
                <Grid.Col width={12}>
                  <h5 className="text-uppercase mb-8">
                    {t('PRIMARY SERVER (MANDATORY)')}
                  </h5>
                </Grid.Col>
              </Grid.Row>
              {renderServerSection({
                prefix: 'primary',
                values,
                errors,
                handleBlur,
                handleChange,
                setFieldValue,
                testState: primaryTestState,
                isTesting: isTestingPrimary,
                setTestState: setPrimaryTestState,
                setIsTesting: setIsTestingPrimary,
                certFiles: primaryCertFiles,
                setCertFiles: setPrimaryCertFiles,
                hasCertificate: rawGroup?.primary?.has_certificate ?? false,
                hasPrivateKey: rawGroup?.primary?.has_private_key ?? false,
                serverId: primaryServerId,
                lastCheckStatus: rawGroup?.primary?.last_connection_check_status ?? null,
                lastCheckedAt: rawGroup?.primary?.last_connection_checked_at ?? null,
              })}

              {/* Secondary server toggle */}
              <Grid.Row>
                <Grid.Col width={12}>
                  <Flex v="center" className="mb-8 mt-16">
                    <Checkbox
                      checked={values.hasSecondary}
                      disabled={disabledEdit}
                      onChange={e => {
                        setFieldValue('hasSecondary', e.target.checked);
                        // Mark as touched so the save button's dirty-check passes
                        // even when this is the only change the user made.
                        setFieldTouched('hasSecondary', true, false);
                      }}
                    />
                    <h5 className="ml-2 text-uppercase" style={{ margin: '0 0 0 8px' }}>
                      {t('SECONDARY SERVER (OPTIONAL)')}
                    </h5>
                  </Flex>
                </Grid.Col>
              </Grid.Row>

              {values.hasSecondary &&
                renderServerSection({
                  prefix: 'secondary',
                  values,
                  errors,
                  handleBlur,
                  handleChange,
                  setFieldValue,
                  testState: secondaryTestState,
                  isTesting: isTestingSecondary,
                  setTestState: setSecondaryTestState,
                  setIsTesting: setIsTestingSecondary,
                  certFiles: secondaryCertFiles,
                  setCertFiles: setSecondaryCertFiles,
                  hasCertificate: rawGroup?.secondary?.has_certificate ?? false,
                  hasPrivateKey: rawGroup?.secondary?.has_private_key ?? false,
                  serverId: secondaryServerId,
                  lastCheckStatus: rawGroup?.secondary?.last_connection_check_status ?? null,
                  lastCheckedAt: rawGroup?.secondary?.last_connection_checked_at ?? null,
                })}

              {/* Save */}
              <Grid.Row>
                <Grid.Col width={12}>
                  <Flex h="end" v="center" className="mt-16">
                    <SubmitButton
                      disabled={isSaveDisabled}
                      loading={isLoadingSave}
                      ghost={testHasErrors}
                      variant={testHasErrors ? 'default' : 'primary'}
                      colorOverrides={
                        testHasErrors
                          ? { text: '#fa4654', border: '#fa4654' }
                          : undefined
                      }
                    >
                      {testHasErrors
                        ? t('Save despite failed test')
                        : t('common:Save')}
                    </SubmitButton>
                  </Flex>
                </Grid.Col>
              </Grid.Row>
            </Form>
          );
        }}
      </Formik>
    </PageContent>
  );
}

export default AddOPCServer;
